import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import TweetForm from "./components/TweetForm";
import TweetList from "./components/TweetList";
import Swal from "sweetalert2";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import ChatRoomWrapper from "./components/ChatRoomWrapper";
import ChatRoomWrapperVol2 from "./components/ChatRoomWrapperVol2";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthAction from "./pages/AuthAction";
import CareerView from "./components/CareerView";
import MarketSitView from "./components/MarketSitView";
import ChatList from "./components/ChatList"; // Aquí cambio ChatView por ChatList
import ProfileModal from "./components/ProfileModal";
import PublicProfileModal from "./components/PublicProfileModal";
import { auth, db, storage } from "./firebase";
import { signOut } from "firebase/auth";
import { getMessaging, isSupported } from "firebase/messaging";
import useNotification from "./hooks/useNotifications";
import { containsBadWords } from "./utils/contentFilter";
import { notifyError, notifyLoadError } from "./utils/errorHandler";
import { motion } from "framer-motion";
import PromotionalPublicProfileModal from "./components/PromotionalPublicProfileModal";
import NotificationHistory from "./components/NotificationHistory";
import MultiHubView from "./components/MultiHubView/MultiHubView";
import PromotionalHome from "./components/PromotionalHome";
import PromoLanding from "./pages/PromoLanding";
import DownloadApp from "./pages/DownloadApp";
import AlertModal from "./components/AlertModal";
import WelcomePendingModal from "./components/WelcomePendingModal";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";


import {
  requestPermissionAndGetToken,
  saveTokenToFirestore,
  onMessageListener,
} from "./firebaseMessaging";
import {
  isNativePlatform,
  setupNativePush,
  removeNativePushListeners,
  extractPathFromUrl,
} from "./utils/nativeNotifications";
import { initStatusBar, hideSplashScreen } from "./utils/capacitorSetup";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  setDoc,
  serverTimestamp,
  doc,
  updateDoc,
  increment,
  deleteField,
  getDocs,
  where,
  limit,
  startAfter,
} from "firebase/firestore";



// ─── Hook: abrir / encontrar chat con otro usuario ───────────────────────────
// Definido FUERA de Home para cumplir las reglas de hooks de React
function useSendMessage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const handleSendMessage = async (otherUser) => {
    if (!currentUser) return;

    try {
      let chatId = null;

      // Buscar chat existente en "chats"
      const chatsRef = collection(db, "chats");
      const q = query(chatsRef, where("users", "array-contains", currentUser.uid));
      const chatsSnap = await getDocs(q);
      chatsSnap.forEach((chatDoc) => {
        const data = chatDoc.data();
        if (data.users.includes(otherUser.uid)) chatId = chatDoc.id;
      });

      // Buscar en "chatsPromotional" si no existe
      if (!chatId) {
        const chatsPromoRef = collection(db, "chatsPromotional");
        const qPromo = query(chatsPromoRef, where("users", "array-contains", currentUser.uid));
        const chatsPromoSnap = await getDocs(qPromo);
        chatsPromoSnap.forEach((chatDoc) => {
          const data = chatDoc.data();
          if (data.users.includes(otherUser.uid)) chatId = chatDoc.id;
        });
      }

      // Si no existe, crear el chat con ID determinístico user1_user2
      if (!chatId) {
        chatId =
          currentUser.uid < otherUser.uid
            ? `${currentUser.uid}_${otherUser.uid}`
            : `${otherUser.uid}_${currentUser.uid}`;

        // Crear el documento en Firestore para que ChatRoomWrapper lo encuentre
        // merge: true evita sobrescribir si por alguna razón ya existiera
        await setDoc(
          doc(db, "chats", chatId),
          {
            users:     [currentUser.uid, otherUser.uid],
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      navigate(`/chat/${chatId}`);
    } catch (error) {
      console.error("Error abriendo chat:", error);
      notifyError("No se pudo abrir el chat.");
    }
  };

  return handleSendMessage;
}

// ─────────────────────────────────────────────────────────────────────────────

function Home() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // ── Inicialización nativa (StatusBar + SplashScreen) ─────────────────────
  useEffect(() => {
    initStatusBar();          // Barra de estado oscura con color de SIT
    hideSplashScreen();       // Oculta el splash cuando React ya montó
  }, []);
  const [tweet, setTweet] = useState("");
  const [tweets, setTweets] = useState([]);
  const [olderTweets, setOlderTweets] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const paginationCursorRef = useRef(null);
  const paginationStartedRef = useRef(false);
  // Detección de cambio de status para notificación de verificación
  const prevStatusRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [media, setMedia] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPublicProfile, setShowPublicProfile] = useState(false);
  const [selectedPromoProfile, setSelectedPromoProfile] = useState(null);
  const [showPromotionalProfileModal, setShowPromotionalProfileModal] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem("darkMode") === "true";});
  const [activeTab, setActiveTab] = useState("general");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [selectedAlert, setSelectedAlert] = useState(null);
  // Aviso legal: se muestra solo la primera vez que el usuario entra
  const [showLegalNotice, setShowLegalNotice] = useState(
    () => !localStorage.getItem("sit_legal_accepted")
  );
  // Fallback local por si falla el write a Firestore
  const [welcomeClosed, setWelcomeClosed] = useState(false);
  const handleSendMessage = useSendMessage();

  // Aquí defines la función para manejar notificaciones recibidas en primer plano
  // useCallback estabiliza la referencia para que useNotifications no registre
  // múltiples listeners en cada re-render de Home
  const onMessageCallback = useCallback((payload) => {
    const title = payload.notification?.title || "Notificación";
    const body = payload.notification?.body || "";
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "info",
      title,
      text: body,
      showConfirmButton: false,
      timer: 5000,
      timerProgressBar: true,
    });
  }, []);

  const { sendNotification } = useNotification(currentUser?.uid, onMessageCallback);

  // Función para cerrar alerta
const handleCloseAlert = () => {
  if (!selectedAlert?.id) return;

  const closedIds = JSON.parse(localStorage.getItem("closedAlertIds") || "[]");
  if (!closedIds.includes(selectedAlert.id)) {
    closedIds.push(selectedAlert.id);
    localStorage.setItem("closedAlertIds", JSON.stringify(closedIds));
  }

  setSelectedAlert(null);
};

//Alertas del sistema
useEffect(() => {
  const q = query(collection(db, "alertas"), orderBy("fechaCreacion", "desc"));
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setAlerts(data);
  });

  return () => unsubscribe();
}, []);

useEffect(() => {
  if (alerts.length === 0) return;

  const closedIds = JSON.parse(localStorage.getItem("closedAlertIds") || "[]");

  const activeAlert = alerts.find(
    (a) => a.activo && !closedIds.includes(a.id)
  );

  if (activeAlert) {
    setSelectedAlert(activeAlert);
  } else {
    setSelectedAlert(null); // No hay alertas nuevas
  }
}, [alerts]);


  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode]);

  // ── Notificación cuando la cuenta pasa de pendiente a verificada ──────────
  useEffect(() => {
    const currentStatus = currentUser?.status;

    if (prevStatusRef.current === null) {
      // Primera carga: solo almacenamos el status sin mostrar nada
      prevStatusRef.current = currentStatus;
      return;
    }

    if (prevStatusRef.current === "pending" && currentStatus === "verified") {
      Swal.fire({
        icon: "success",
        title: "¡Cuenta verificada!",
        text: "Tu cuenta fue aprobada. Ya tienes acceso completo a SIT.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 6000,
        timerProgressBar: true,
      });
    }

    prevStatusRef.current = currentStatus;
  }, [currentUser?.status]);

  useEffect(() => {
    if (!currentUser) return;

    let unsubscribeOnMessage = null;

    const setupFCM = async () => {
      // ── App nativa Android (Capacitor) ──────────────────────────────────────
      if (isNativePlatform()) {
        await setupNativePush({
          // Token FCM recibido → guardar en Firestore (mismo lugar que en web)
          onToken: async (token) => {
            await saveTokenToFirestore(currentUser.uid, token);
          },

          // Notificación con app en primer plano → mostrar toast
          onForeground: (notification) => {
            Swal.fire({
              toast: true,
              position: "top-end",
              icon: "info",
              title: notification.title || "Notificación",
              text: notification.body || "",
              showConfirmButton: false,
              timer: 5000,
              timerProgressBar: true,
            });
          },

          // Usuario tocó la notificación → navegar a la ruta correcta
          onTap: (data) => {
            const path = extractPathFromUrl(data?.url || data?.path || "");
            if (path) navigate(path);
          },
        });
        return;
      }

      // ── Web / PWA ───────────────────────────────────────────────────────────
      try {
        const supported = await isSupported();
        if (!supported) {
          console.warn("[FCM] No compatible con este navegador.");
          return;
        }

        const token = await requestPermissionAndGetToken();
        if (token) {
          await saveTokenToFirestore(currentUser.uid, token);
        }

        unsubscribeOnMessage = onMessageListener((payload) => {
          const title = payload.notification?.title || "Notificación";
          const body  = payload.notification?.body  || "";
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "info",
            title,
            text: body,
            showConfirmButton: false,
            timer: 5000,
            timerProgressBar: true,
          });
        });
      } catch (error) {
        console.error("[FCM] Error configurando notificaciones web:", error);
      }
    };

    setupFCM();

    return () => {
      if (unsubscribeOnMessage) unsubscribeOnMessage();
      if (isNativePlatform()) removeNativePushListeners();
    };
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helper: carga replies para un array de tweets ────────────────────────
  const loadRepliesForTweets = async (tweetDocs) => {
    return Promise.all(
      tweetDocs.map(async (tweet) => {
        try {
          const repliesRef = collection(db, "tweets", tweet.id, "replies");
          const replySnapshot = await getDocs(repliesRef);
          const replies = replySnapshot.docs
            .map((d) => ({
              id: d.id,
              ...d.data(),
              date: d.data().date?.toDate?.() || new Date(),
            }))
            .filter((r) => !r.deleted)
            .sort((a, b) => b.date - a.date);
          return { ...tweet, replies };
        } catch {
          return { ...tweet, replies: [] };
        }
      })
    );
  };

  useEffect(() => {
    if (activeTab !== "general") return;

    // Resetear paginación al entrar al tab
    setOlderTweets([]);
    paginationCursorRef.current = null;
    paginationStartedRef.current = false;
    setHasMore(false);

    const LIMIT = 30;
    const q = query(
      collection(db, "tweets"),
      orderBy("date", "desc"),
      limit(LIMIT)
    );

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const tweetData = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((t) => !t.deleted);

        const tweetsWithReplies = await loadRepliesForTweets(tweetData);
        setTweets(tweetsWithReplies);

        // Actualizar cursor solo si el usuario no ha paginado aún
        if (!paginationStartedRef.current) {
          paginationCursorRef.current = snapshot.docs[snapshot.docs.length - 1] || null;
          setHasMore(snapshot.docs.length === LIMIT);
        }
      },
      (error) => {
        console.error("Error al cargar tweets:", error.code, error.message);
      }
    );

    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ── Cargar más tweets (paginación) ───────────────────────────────────────
  // Sigue cargando tandas de 30 automáticamente hasta encontrar al menos
  // un tweet visible (no borrado), evitando que el usuario tenga que
  // hacer clic varias veces cuando hay muchos docs con deleted:true.
  const loadMoreTweets = async () => {
    if (!paginationCursorRef.current || loadingMore) return;
    setLoadingMore(true);
    paginationStartedRef.current = true;

    try {
      const LIMIT = 30;
      const MAX_BATCHES = 5; // límite de seguridad para no hacer bucle infinito
      let accumulated = [];
      let cursor = paginationCursorRef.current;
      let exhausted = false;

      for (let i = 0; i < MAX_BATCHES; i++) {
        const snapshot = await getDocs(
          query(
            collection(db, "tweets"),
            orderBy("date", "desc"),
            startAfter(cursor),
            limit(LIMIT)
          )
        );

        if (snapshot.empty) {
          exhausted = true;
          break;
        }

        const visible = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((t) => !t.deleted);

        const withReplies = await loadRepliesForTweets(visible);
        accumulated = [...accumulated, ...withReplies];
        cursor = snapshot.docs[snapshot.docs.length - 1];

        if (snapshot.docs.length < LIMIT) {
          // Última página — no quedan más docs
          exhausted = true;
          break;
        }

        // Ya tenemos tweets visibles → no hace falta seguir cargando
        if (accumulated.length > 0) break;
      }

      setOlderTweets((prev) => [...prev, ...accumulated]);
      paginationCursorRef.current = cursor;
      setHasMore(!exhausted);
    } catch (error) {
      console.error("Error cargando más tweets:", error);
      notifyLoadError("No se pudieron cargar más tweets.");
    } finally {
      setLoadingMore(false);
    }
  };
  
  const searchContainerRef       = useRef(); // desktop (right sidebar)
  const searchContainerMobileRef = useRef(); // mobile (top bar)

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inDesktop = searchContainerRef.current?.contains(event.target);
      const inMobile  = searchContainerMobileRef.current?.contains(event.target);
      if (!inDesktop && !inMobile) {
        setSearchResults([]);
      }
    };

    // mousedown → desktop/web  |  touchstart → mobile tap
    document.addEventListener("mousedown",  handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener("mousedown",  handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  
const handleTweet = async () => {
  const trimmedTweet = tweet.trim();

if (!trimmedTweet)
  return Swal.fire("Oops", "El tweet está vacío.", "warning");

// Normaliza: minúsculas y elimina espacios internos
const normalizedText = trimmedTweet.toLowerCase().replace(/\s+/g, "");

if (containsBadWords(normalizedText)) {
  return Swal.fire(
    "Lenguaje no permitido",
    "Tu publicación contiene palabras ofensivas o inapropiadas. Por favor, modifícala antes de tweetear.",
    "error"
  );
}

  
  const newTweet = {
    content: tweet.trim(),
    date: serverTimestamp(),
    likes: { count: 0, users: {} },
    media: media.length > 0 ? media : null,
    replies: [],
    userName: currentUser?.displayName || currentUser?.email || "Anónimo",
    userId: currentUser?.uid,
    deleted: false,
  };

try {
  // Guardar el tweet y obtener la referencia del documento
  const tweetRef = await addDoc(collection(db, "tweets"), newTweet);

  // Limpiar estado y mostrar éxito
  setTweet("");
  setMedia([]);
  Swal.fire("Éxito", "Tweet publicado correctamente", "success");

  // Preparar payload para notificación
  const payload = {
    authorId: currentUser?.uid,
    title: "📢 Nuevo Tweet publicado! (SIT)",
    body: `${newTweet.userName} publicó un nuevo tweet.`,
    tweetId: tweetRef.id // Agregamos el ID del tweet
  };

  // Verificar y enviar notificación
  if (!payload.authorId) {
    console.error("No hay authorId, no se puede enviar notificación");
  } else {
    await sendNotification(payload); // Tu función ya debe aceptar tweetId en el backend
  }

} catch (error) {
  console.error("Error guardando tweet o enviando notificación:", error);
  Swal.fire("Error", "No se pudo publicar el tweet.", "error");
}
};

  const handleDelete = async (id) => {
    Swal.fire({
      title: "¿Eliminar este tweet?",
      text: "No podrás revertir esto",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const tweetRef = doc(db, "tweets", id);
        try {
          await updateDoc(tweetRef, { deleted: true });
        } catch (error) {
          console.error("Error marcando como eliminado el tweet:", error);
          notifyError("No se pudo eliminar el tweet.");
        }
      }
    });
  };

  const handleLike = async (tweetId, likes) => {
    if (!currentUser) return Swal.fire({ toast: true, position: "top-end", icon: "warning", title: "Debes iniciar sesión para dar like.", showConfirmButton: false, timer: 3000 });
    const tweetRef = doc(db, "tweets", tweetId);
    const userId = currentUser.uid;
    const displayName = currentUser.displayName || "Anónimo";
    const hasLiked = likes?.users?.[userId];

    try {
      if (hasLiked) {
        await updateDoc(tweetRef, {
          [`likes.users.${userId}`]: deleteField(),
          "likes.count": increment(-1),
        });
      } else {
        await updateDoc(tweetRef, {
          [`likes.users.${userId}`]: { name: displayName },
          "likes.count": increment(1),
        });
      }
    } catch (error) {
      console.error("Error al actualizar like:", error);
      notifyError("No se pudo actualizar el like.");
    }
  };

  const handleReply = async (tweetId, replyText) => {
    if (!replyText.trim()) return;

    const reply = {
      text: replyText.trim(),
      date: serverTimestamp(),
      userId: currentUser.uid,
      userName: currentUser.displayName || currentUser.email || "Anónimo",
      deleted: false,
    };

    try {
      const tweetRef = doc(db, "tweets", tweetId);
      await addDoc(collection(tweetRef, "replies"), reply);
      const replySnapshot = await getDocs(collection(tweetRef, "replies"));
      const updatedReplies = replySnapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate?.() || new Date(),
        }))
        .filter((reply) => !reply.deleted)
        .sort((a, b) => b.date - a.date);

      setTweets((prevTweets) =>
        prevTweets.map((tweet) =>
          tweet.id === tweetId ? { ...tweet, replies: updatedReplies } : tweet
        )
      );
    } catch (error) {
      console.error("Error agregando reply:", error);
      notifyError("No se pudo enviar la respuesta.");
    }
  };

  const handleDeleteReply = async (tweetId, replyId) => {
    try {
      const result = await Swal.fire({
        title: "¿Eliminar este comentario?",
        text: "No podrás revertir esto",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, eliminar",
      });

      if (result.isConfirmed) {
        const replyRef = doc(db, "tweets", tweetId, "replies", replyId);
        await updateDoc(replyRef, { deleted: true });

        const replySnapshot = await getDocs(
          collection(db, "tweets", tweetId, "replies")
        );
        const updatedReplies = replySnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
            date: doc.data().date?.toDate?.() || new Date(),
          }))
          .filter((reply) => !reply.deleted)
          .sort((a, b) => b.date - a.date);

        setTweets((prevTweets) =>
          prevTweets.map((tweet) =>
            tweet.id === tweetId ? { ...tweet, replies: updatedReplies } : tweet
          )
        );

        await Swal.fire(
          "Eliminado",
          "El comentario fue eliminado correctamente.",
          "success"
        );
      }
    } catch (error) {
      console.error("Error eliminando comentario:", error);
      Swal.fire("Error", "No se pudo eliminar el comentario.", "error");
    }
  };

  const handleImageUpload = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  setLoading(true);

  try {
    // Crear ruta única en Firebase Storage, por ejemplo "tweets"
    const filePath = `tweets/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, filePath);

    // Subir el archivo
    const snapshot = await uploadBytes(storageRef, file);

    // Obtener la URL pública
    const downloadURL = await getDownloadURL(snapshot.ref);

    // Guardar en el estado media
    setMedia((prev) => [...prev, downloadURL]);
  } catch (error) {
    console.error("Error al cargar la imagen en Firebase:", error);
    notifyError("No se pudo cargar la imagen. Verifica el formato e intenta de nuevo.");
  } finally {
    setLoading(false);
  }
};

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    const fetchUsersAndPromoProfiles = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, "users"));
        const users = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Hacemos TODAS las consultas a promoProfiles en paralelo
        const promoProfilesSnapshots = await Promise.all(
          users.map((user) =>
            getDocs(collection(db, "users", user.id, "promoProfiles")).then((snapshot) => ({
              userId: user.id,
              docs: snapshot.docs,
            }))
          )
        );

        // Extraemos perfiles promocionales que coincidan con el término (en name o en tags)
        const promoProfiles = [];
        promoProfilesSnapshots.forEach(({ userId, docs }) => {
          docs.forEach((promoDoc) => {
            const promoData = promoDoc.data();
            const searchLower = searchTerm.toLowerCase();

            // Buscamos si el name incluye el término
            const nameMatch = promoData.name?.toLowerCase().includes(searchLower);

            // Buscamos si algún tag incluye el término
            const tagsMatch =
              Array.isArray(promoData.tags) &&
              promoData.tags.some((tag) => tag.toLowerCase().includes(searchLower));

            if (nameMatch || tagsMatch) {
              promoProfiles.push({
                id: promoDoc.id,
                userId,
                type: "promo",
                ...promoData,
              });
            }
          });
        });
        // Usuarios que coincidan con el nombre
        const matchedUsers = users
          .filter((user) =>
            user.nombre?.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .map((user) => ({ ...user, type: "user" }));

        // Unimos ambos resultados
        setSearchResults([...matchedUsers, ...promoProfiles]);
      } catch (error) {
        console.error("Error buscando usuarios y perfiles promocionales:", error);
        notifyLoadError("No se pudieron cargar los resultados de búsqueda.");
      }
    };

    fetchUsersAndPromoProfiles();
  }, [searchTerm]);

  // ── Bloqueo total para cuentas rechazadas ──────────────────────────────
  if (currentUser?.status === "rejected") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
          {/* Header rojo */}
          <div className="bg-gradient-to-r from-red-500 to-red-700 px-6 py-5 flex flex-col items-center gap-2">
            <span className="text-5xl">🚫</span>
            <h2 className="text-xl font-extrabold text-white text-center">Cuenta no autorizada</h2>
          </div>
          {/* Cuerpo */}
          <div className="px-6 py-6 space-y-4 text-sm text-gray-700 dark:text-gray-300 text-center">
            <p className="text-base font-semibold text-gray-800 dark:text-white">
              Tu solicitud de acceso a <span className="text-red-500 font-bold">SIT</span> fue rechazada.
            </p>
            <p>
              Esto puede deberse a que la información proporcionada durante el registro no pudo ser validada por nuestro equipo de moderación.
            </p>
            <p>
              Si crees que esto es un error o deseas que revisemos tu caso, comunícate con soporte a través del correo:
            </p>
            <a
              href="mailto:soporte@sit-app.com"
              className="inline-block bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 font-semibold px-4 py-2 rounded-xl border border-red-200 dark:border-red-700 hover:bg-red-100 transition"
            >
              📧 soporte@sit-app.com
            </a>
            <p className="text-xs text-gray-500 dark:text-gray-400 pt-1">
              Por el momento no tienes acceso a la plataforma. Una vez que tu caso sea revisado y aprobado, podrás ingresar sin inconvenientes.
            </p>
          </div>
          {/* Footer */}
          <div className="px-6 pb-6">
            <button
              onClick={() => signOut(auth)}
              className="w-full bg-gradient-to-r from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 text-white font-bold py-3 rounded-xl transition shadow-md"
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Shared search-results renderer ────────────────────────────────────────
  const renderSearchResults = () =>
    searchResults.length > 0 ? (
      <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-60 overflow-y-auto">
        {searchResults.map((result) => (
          <div
            key={result.id + result.type}
            className="flex justify-between items-center px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
          >
            <div className="flex items-center space-x-3">
              {(result.type === "promo" || result.type === "user") && (
                <img
                  src={
                    result.type === "promo"
                      ? result.profilePhotoUrl
                      : result.photoURL || "/default-avatar.png"
                  }
                  alt={result.type === "promo" ? result.name : result.nombre}
                  className="w-8 h-8 rounded-full object-cover"
                />
              )}
              <div>
                <p className="font-medium text-sm">
                  {result.type === "promo" ? result.name : result.nombre}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {result.type === "promo" ? "Perfil promocional" : `@${result.nickname}`}
                </p>
              </div>
            </div>
            <div className="flex space-x-2">
              {result.type === "promo" ? (
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    const promoProfileWithUid = { ...result, uid: result.id };
                    setSelectedPromoProfile(promoProfileWithUid);
                    setShowPromotionalProfileModal(true);
                  }}
                  className="text-blue-500 text-sm hover:underline"
                >
                  Ver perfil
                </button>
              ) : (
                <>
                  <button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setSelectedUser({ ...result, uid: result.id });
                      setShowPublicProfile(true);
                    }}
                    className="text-blue-500 text-sm hover:underline"
                  >
                    Ver perfil
                  </button>
                  <button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSendMessage({ ...result, uid: result.id })}
                    className="text-green-500 text-sm hover:underline"
                  >
                    Mensaje
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    ) : null;

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 dark:text-white">

      {/* ═══════════════════════════════════════════════════════════════
          PAGE LAYOUT — mobile: single column  |  lg+: 3-column sidebar
          ═══════════════════════════════════════════════════════════════ */}
      <div className="p-4 sm:p-6 lg:p-0 lg:max-w-[1300px] lg:mx-auto lg:flex lg:items-start lg:gap-5 lg:px-5 lg:py-6">

        {/* ─────────── LEFT SIDEBAR (desktop only) ─────────── */}
        <aside className="hidden lg:flex flex-col gap-3 w-60 xl:w-64 shrink-0 sticky top-6 self-start">

          {/* Profile card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-5 flex flex-col items-center text-center gap-3">
            <button
              onClick={() => setIsModalOpen(true)}
              className="relative group"
              title="Editar perfil"
            >
              <img
                src={
                  currentUser?.photoURL ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    currentUser?.displayName || "U"
                  )}&background=6366f1&color=fff&size=96`
                }
                alt="Avatar"
                className="w-16 h-16 rounded-full ring-4 ring-purple-100 dark:ring-purple-900 object-cover group-hover:ring-purple-300 transition-all duration-200"
              />
              <span className="absolute -bottom-1 -right-1 bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold shadow">
                ✎
              </span>
            </button>
            <div>
              <p className="font-bold text-gray-900 dark:text-white text-sm leading-tight">
                {currentUser?.displayName || "Usuario"}
              </p>
              {currentUser?.nickname && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  @{currentUser.nickname}
                </p>
              )}
              {currentUser?.carrera && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {currentUser.carrera}
                </p>
              )}
            </div>
            {currentUser?.status === "pending" && (
              <span className="text-[11px] bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-700 px-2.5 py-1 rounded-full font-semibold">
                ⏳ Pendiente de verificación
              </span>
            )}
            {currentUser?.status === "verified" && (
              <span className="text-[11px] bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700 px-2.5 py-1 rounded-full font-semibold">
                ✅ Cuenta verificada
              </span>
            )}
          </div>

          {/* Navigation */}
          <nav className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-2 flex flex-col gap-0.5">
            {[
              { id: "general",     icon: "🏠", label: "General" },
              { id: "mi-carrera",  icon: "🎓", label: "Mi Carrera" },
              { id: "market-sit",  icon: "🛒", label: "Market-SIT" },
              { id: "chatlist",    icon: "💬", label: "Chat" },
              { id: "multihub",    icon: "🎮", label: "MultiHub" },
            ].map(({ id, icon, label }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-150 w-full text-left ${
                  activeTab === id
                    ? "bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/20 text-blue-600 dark:text-blue-400 shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/60"
                }`}
              >
                <span className="text-base w-5 text-center">{icon}</span>
                <span className="flex-1">{label}</span>
                {activeTab === id && (
                  <span className="w-1.5 h-4 bg-blue-500 dark:bg-blue-400 rounded-full" />
                )}
              </button>
            ))}
          </nav>

          {/* Controls */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-2 flex flex-col gap-0.5">
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-all w-full text-left"
            >
              <span className="text-base w-5 text-center">{darkMode ? "☀️" : "🌙"}</span>
              {darkMode ? "Modo Claro" : "Modo Oscuro"}
            </button>
            <button
              onClick={() => signOut(auth)}
              className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all w-full text-left"
            >
              <span className="text-base w-5 text-center">🚪</span>
              Cerrar sesión
            </button>
          </div>
        </aside>

        {/* ─────────── CENTER COLUMN (always visible) ─────────── */}
        <div className="flex-1 min-w-0">
          <div className="max-w-xl mx-auto bg-white dark:bg-gray-800 p-4 rounded-xl shadow lg:max-w-none lg:mx-0">

            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <motion.h2
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, type: "spring", bounce: 0.4 }}
                whileHover={{ scale: 1.05 }}
                className="text-2xl md:text-4xl font-extrabold tracking-wide text-center"
              >
                <motion.span
                  className="text-yellow-500"
                  whileHover={{ scale: 1.2, rotate: -5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  SIT
                </motion.span>
              </motion.h2>
              {/* Avatar & dark-mode: only shown on mobile (sidebar handles desktop) */}
              <button onClick={() => setIsModalOpen(true)} className="lg:hidden">
                <img
                  src={
                    currentUser?.photoURL ||
                    `https://ui-avatars.com/api/?name=${
                      currentUser?.displayName || "User"
                    }`
                  }
                  alt="Avatar"
                  className="w-10 h-10 rounded-full"
                />
              </button>
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="px-3 py-1 text-sm rounded bg-gray-200 dark:bg-gray-700 lg:hidden"
              >
                {darkMode ? "☀️ Claro" : "🌙 Oscuro"}
              </button>
            </div>

            {/* Search bar (mobile) — hidden on lg+ because right sidebar shows it */}
            <div className="relative mb-4 flex items-center gap-3 lg:hidden" ref={searchContainerMobileRef}>
              <div className="relative flex-grow">
                <input
                  type="text"
                  placeholder="Buscar perfiles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 pl-10 border rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <span className="absolute left-3 top-2.5 text-gray-500 dark:text-gray-300">🔍</span>
                {renderSearchResults()}
              </div>
              <NotificationHistory />
            </div>

            {/* Tabs */}
            <div
              className="flex space-x-2 mb-4 border-b border-gray-300 dark:border-gray-600 overflow-x-auto scrollbar-thin scrollbar-thumb-rounded scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              {["general", "mi-carrera", "market-sit", "chatlist", "multihub"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-shrink-0 py-3 px-5 text-base md:text-lg font-semibold border-b-2 focus:outline-none transition-colors duration-200 ${
                    activeTab === tab
                      ? "border-blue-500 text-blue-600 dark:text-blue-400"
                      : "border-transparent text-gray-600 dark:text-gray-400 hover:text-blue-500"
                  }`}
                >
                  {tab === "general"
                    ? "GENERAL"
                    : tab === "mi-carrera"
                    ? "MI CARRERA"
                    : tab === "market-sit"
                    ? "MARKET-SIT"
                    : tab === "chatlist"
                    ? "CHAT"
                    : "MULTIHUB"}
                </button>
              ))}
            </div>

            {/* ── Banner de verificación pendiente ── */}
            {currentUser?.status === "pending" && (
              <div className="mb-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-600 rounded-xl px-4 py-3 flex items-start gap-3">
                <span className="text-2xl mt-0.5">⏳</span>
                <div>
                  <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-200">
                    Tu cuenta está pendiente de verificación
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-0.5">
                    Estamos revisando tu información. Este proceso toma entre{" "}
                    <strong>1 y 6 horas</strong>. Mientras tanto puedes explorar la plataforma en modo lectura.
                  </p>
                </div>
              </div>
            )}

            {activeTab === "general" && (
              <>
                {currentUser?.status === "pending" ? (
                  <div className="mb-4 bg-gray-100 dark:bg-gray-700 border border-dashed border-gray-300 dark:border-gray-500 rounded-xl px-4 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    🔒 Podrás publicar una vez que tu cuenta sea verificada.
                  </div>
                ) : (
                  <TweetForm
                    tweet={tweet}
                    setTweet={setTweet}
                    handleTweet={handleTweet}
                    onImageUpload={handleImageUpload}
                    media={media}
                    setMedia={setMedia}
                    loading={loading}
                  />
                )}
                <TweetList
                  tweets={[...tweets, ...olderTweets]}
                  onLike={handleLike}
                  onDelete={handleDelete}
                  onReply={handleReply}
                  onDeleteReply={handleDeleteReply}
                  currentUser={currentUser}
                />
                {hasMore && (
                  <div className="flex justify-center mt-4 mb-2">
                    <button
                      onClick={loadMoreTweets}
                      disabled={loadingMore}
                      className="px-6 py-2 rounded-xl bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {loadingMore ? "Cargando..." : "Cargar más tweets"}
                    </button>
                  </div>
                )}
              </>
            )}

            {activeTab === "mi-carrera" && <CareerView />}
            {activeTab === "market-sit" && <MarketSitView />}
            {activeTab === "chatlist" &&
              (currentUser?.status === "pending" ? (
                <div className="mt-10 flex flex-col items-center gap-3 text-center px-4">
                  <span className="text-5xl">🔒</span>
                  <p className="text-base font-semibold text-gray-700 dark:text-gray-300">
                    El chat estará disponible una vez verificada tu cuenta
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    El proceso de verificación toma entre 1 y 6 horas.
                  </p>
                </div>
              ) : (
                <ChatList />
              ))}
            {activeTab === "multihub" && <MultiHubView />}
          </div>
        </div>

        {/* ─────────── RIGHT SIDEBAR (desktop only) ─────────── */}
        <aside className="hidden lg:flex flex-col gap-3 w-64 xl:w-72 shrink-0 sticky top-6 self-start">

          {/* Search + Notifications */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-4">
            <div className="relative" ref={searchContainerRef}>
              <input
                type="text"
                placeholder="Buscar perfiles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pl-10 border rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
              />
              <span className="absolute left-3 top-2.5 text-gray-500 dark:text-gray-300 text-sm">🔍</span>
              {renderSearchResults()}
            </div>
            <div className="flex justify-end mt-3">
              <NotificationHistory />
            </div>
          </div>

          {/* About SIT */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg font-extrabold text-yellow-500 tracking-wider">SIT</span>
              <span className="text-[10px] font-bold bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 px-2 py-0.5 rounded-full uppercase tracking-wide">
                Beta
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
              Social Interactive Technology — una plataforma para conectar estudiantes y docentes universitarios.
            </p>
            <div className="flex flex-col gap-2">
              {[
                { icon: "📰", label: "Feed universitario en tiempo real" },
                { icon: "🎓", label: "Contenido por carrera" },
                { icon: "🛒", label: "Mercado estudiantil" },
                { icon: "💬", label: "Chat en tiempo real" },
                { icon: "🎮", label: "MultiHub: juegos, biblioteca y más" },
              ].map(({ icon, label }) => (
                <div key={label} className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span className="mt-0.5 shrink-0">{icon}</span>
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tip card */}
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl border border-indigo-100 dark:border-indigo-800/50 p-4">
            <p className="text-xs font-bold text-indigo-600 dark:text-indigo-400 mb-1.5 uppercase tracking-wide">
              💡 Tip
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
              Visita <strong className="text-indigo-600 dark:text-indigo-300">MultiHub</strong> para acceder a la Biblioteca compartida, juegos, música y mucho más contenido exclusivo de la comunidad.
            </p>
          </div>

          {/* Footer links */}
          <p className="text-[11px] text-gray-400 dark:text-gray-600 text-center px-2 leading-relaxed">
            © {new Date().getFullYear()} SIT — Social Interactive Technology.
            Plataforma en fase beta.
          </p>
        </aside>
      </div>

      {/* ══════════════════════════════════════════
          MODALS — unchanged, outside the layout grid
          ══════════════════════════════════════════ */}

      {currentUser?.status === "pending" &&
        !currentUser?.welcomeShown &&
        !welcomeClosed && (
          <WelcomePendingModal onClose={() => setWelcomeClosed(true)} />
        )}

      <ProfileModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <PublicProfileModal
        isOpen={showPublicProfile}
        onClose={() => setShowPublicProfile(false)}
        user={selectedUser}
      />
      {showPromotionalProfileModal && selectedPromoProfile && (
        <PromotionalPublicProfileModal
          user={selectedPromoProfile}
          promoProfileId={selectedPromoProfile.id}
          onClose={() => setShowPromotionalProfileModal(false)}
        />
      )}
      {selectedAlert && (
        <AlertModal
          alert={selectedAlert}
          onClose={handleCloseAlert}
        />
      )}

      {/* ── Modal de aviso legal (primera visita) ── */}
      {showLegalNotice && (
        <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="bg-yellow-400 px-6 py-4 flex items-center gap-3">
              <span className="text-2xl">⚠️</span>
              <h2 className="text-lg font-extrabold text-black">Aviso Importante — SIT Beta</h2>
            </div>
            <div className="px-6 py-5 space-y-3 text-sm text-gray-700 dark:text-gray-300 max-h-[55vh] overflow-y-auto">
              <p>
                <strong className="text-gray-900 dark:text-white">SIT (Social Interactive Technology)</strong> es una plataforma digital independiente en fase beta, desarrollada con fines de prueba, validación y mejora continua.
              </p>
              <p>
                Esta plataforma{" "}
                <strong className="text-gray-900 dark:text-white">no está afiliada, asociada ni respaldada oficialmente</strong>{" "}
                por ninguna institución educativa, incluyendo universidades donde puedan encontrarse usuarios registrados.
              </p>
              <p>
                El uso de SIT es completamente{" "}
                <strong className="text-gray-900 dark:text-white">voluntario</strong>. Cada usuario es responsable del contenido que publica, comparte o comunica dentro de la plataforma.
              </p>
              <p>
                SIT cuenta con sistemas de{" "}
                <strong className="text-gray-900 dark:text-white">moderación, monitoreo y reporte</strong>{" "}
                con el objetivo de mantener un entorno seguro y respetuoso. El uso indebido puede conllevar la suspensión o eliminación de cuentas.
              </p>
              <p>
                Al utilizar SIT, aceptas estos términos y te comprometes a hacer un{" "}
                <strong className="text-gray-900 dark:text-white">uso responsable</strong> de la plataforma.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700">
              <button
                onClick={() => {
                  localStorage.setItem("sit_legal_accepted", "true");
                  setShowLegalNotice(false);
                }}
                className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-3 rounded-xl transition"
              >
                Entendido, continuar a la plataforma
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// AuthContext ya incluye el campo `role` via onSnapshot en tiempo real,
// por lo que no necesitamos hacer una consulta extra a Firestore aquí.
function RoleBasedHomeRouter() {
  const { currentUser } = useAuth();

  if (!currentUser) return null;

  const isPromo =
    currentUser.role === "promocional" || currentUser.role === "promotional";

  return isPromo ? (
    <PromotionalHome currentUser={currentUser} />
  ) : (
    <Home />
  );
}


export default function HomeWrapper() {
  const { currentUser } = useAuth();

  return (
    <Routes>
      {!currentUser ? (
        <>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/auth/action" element={<AuthAction />} />
          <Route path="/perfil-promocional" element={<PromoLanding />} />
          <Route path="/download" element={<DownloadApp />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </>
      ) : (
        <>
          <Route path="/" element={<RoleBasedHomeRouter />} />
          <Route path="/auth/action" element={<AuthAction />} />
          <Route path="/chat/:chatId" element={<ChatRoomWrapper />} />
          <Route path="/chat-vol2/:chatId" element={<ChatRoomWrapperVol2 />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/register" element={<Navigate to="/" replace />} />
          <Route path="/perfil-promocional" element={<PromoLanding />} />
          <Route path="/download" element={<DownloadApp />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </>
      )}
    </Routes>
  );
}
